import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import axiosInstance from "../../api/axiosInstance";
import { ASSETS } from "../../config/assets";
import { useAlert } from "../../context/AlertContext";
import { useAuth } from "../../context/AuthContext";
import { useChat } from "../../context/ChatContext";
import { buildRoute, ROUTES } from "../../routes/RouteNames";

import Path from "../../components/Path";
import UserBasicInfo from "../../components/UserBasicInfo";
import UserMoreInfo from "../../components/UserMoreInfo";
import JobItem from "../../components/JobItem";
import JobApplicationsManager from "../../components/JobApplicationsManager";
import "./JobDetail.css";

const JobDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    const { showAlert } = useAlert();
    const { openChatWith, sendMessage } = useChat();

    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedPositionId, setSelectedPositionId] = useState(null);
    const [applicationsData, setApplicationsData] = useState(null);
    const [optimisticallyApplied, setOptimisticallyApplied] = useState({});

    const isOwner =
        currentUser && String(currentUser.id) === String(job?.user_id);

    const handleShareClick = (positionId) => {
        const url = `${window.location.origin}${window.location.pathname}#position-${positionId}`;
        navigator.clipboard.writeText(url)
            .then(() => showAlert("success", "Odkaz zkopírován do schránky!"))
            .catch(() => showAlert("error", "Kopírování selhalo."));
    };

    const handleEditJobClick = () => {
        navigate(buildRoute(ROUTES.EDIT_JOB, { id: String(job.id) }));
    };

    const handleToggleJobActive = async () => {
        try {
            const newStatus = job.status === "published" ? "hidden" : "published";
            await axiosInstance.put(`/api/jobs/${job.id}`, {
                title: job.title,
                description: job.description,
                location_name: job.location_name,
                location_type: job.location_type,
                city: job.city,
                starts_at: job.starts_at,
                starts_time: job.starts_time,
                ends_at: job.ends_at,
                ends_time: job.ends_time,
                duration_type: job.duration_type,
                status: newStatus,
                positions: job.positions,
            });
            showAlert(
                "success",
                job.status === "published"
                    ? "Nabídka byla skrytá."
                    : "Nabídka byla zobrazena.",
            );
            await fetchJob();
        } catch (err) {
            console.error("Chyba při změně stavu nabídky:", err);
            showAlert("error", "Nepodařilo se změnit stav nabídky.");
        }
    };

    const handleDeleteJobClick = async () => {
        if (!window.confirm("Opravdu chcete smazat tuto nabídku? Tato akce je nevratná.")) {
            return;
        }
        try {
            await axiosInstance.delete(`/api/jobs/${job.id}`);
            showAlert("success", "Nabídka byla smazána.");
            navigate(buildRoute(ROUTES.JOBS));
        } catch (err) {
            console.error("Chyba při mazání nabídky:", err);
            showAlert("error", "Nepodařilo se smazat nabídku.");
        }
    };

    const handleShareJobClick = () => {
        const url = `${window.location.origin}${buildRoute(ROUTES.JOB_DETAIL, { id: String(job.id) })}`;
        navigator.clipboard.writeText(url)
            .then(() => showAlert("success", "Odkaz byl zkopírován do schránky!"))
            .catch(() => showAlert("error", "Kopírování selhalo."));
    };

    const handlePositionClick = (positionId) => {
        setSelectedPositionId(positionId);
        window.history.pushState(
            null,
            "",
            `#position-${positionId}`,
        );
    };

    useEffect(() => {
        const hash = window.location.hash;
        if (hash && hash.startsWith("#position-")) {
            const positionId = hash.replace("#position-", "");
            if (job) {
                setSelectedPositionId(positionId);
                setTimeout(() => {
                    const el = document.getElementById(`position-card-${positionId}`);
                    if (el) {
                        el.scrollIntoView({ behavior: "smooth", block: "center" });
                    }
                }, 300);
            }
        }
    }, [job]);

    const fetchJob = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get(`/api/jobs/${id}`);
            setJob(res.data);
            if (res.data.positions && res.data.positions.length > 0) {
                setSelectedPositionId((prev) => prev || res.data.positions[0].id);
            }
        } catch (err) {
            console.error("Chyba při načítání:", err);
            setJob(null);
        } finally {
            setLoading(false);
        }
    }, [id]);

    const fetchApplications = useCallback(async () => {
        if (!isOwner) return;
        try {
            const res = await axiosInstance.get(`/api/jobs/${id}/applications`);
            setApplicationsData(res.data);
        } catch (err) {
            console.error("Chyba při načítání přihlášení:", err);
        }
    }, [id, isOwner]);

    useEffect(() => {
        fetchJob();
    }, [fetchJob]);

    useEffect(() => {
        if (isOwner && job) {
            fetchApplications();
        }
    }, [isOwner, job, fetchApplications]);

    const handleInterestClick = async (position, e) => {
        e.stopPropagation();

        if (position.confirmed_count >= position.required_count) {
            showAlert("info", "Tato pozice je již obsazená.");
            return;
        }

        if (position.is_user_applied) {
            showAlert("info", "Již jste přihlášeni na tuto pozici.");
            return;
        }

        setOptimisticallyApplied((prev) => ({
            ...prev,
            [position.id]: true,
        }));

        try {
            await axiosInstance.post(`/api/jobs/${id}/apply/${position.id}`);
        } catch (err) {
            console.error("Chyba při přihlášení:", err);
            showAlert("error", "Nepodařilo se odeslat přihlášku.");
            setOptimisticallyApplied((prev) => {
                const p = { ...prev };
                delete p[position.id];
                return p;
            });
            return;
        }

        const workerName = currentUser
            ? `${currentUser.first_name || ""} ${currentUser.last_name || ""}`.trim()
            : "Pracovník";

        const messageBody = `📋 ${workerName} se přihlál na pozici ${position.role_label} u nabídky ${job.title}.`;

        const context = {
            subject_type: "item",
            subject_id: job.id,
            context_title: position.role_label,
            context_url: `${window.location.origin}${buildRoute(ROUTES.JOB_DETAIL, { id: String(job.id) })}#position-${position.id}`,
        };

        try {
            const conv = await openChatWith(job.user_id, context);
            if (conv) {
                await sendMessage(messageBody);
            }
        } catch (err) {
            console.error("Chyba při otvírání chatu:", err);
        }
    };


    const handleCancelInterest = async (position, e) => {
        e.stopPropagation();

        if (!position.is_user_applied && !optimisticallyApplied[position.id]) return;

        try {
            await axiosInstance.put(`/api/jobs/${id}/apply/${position.id}`);
            setOptimisticallyApplied((prev) => {
                const p = { ...prev };
                delete p[position.id];
                return p;
            });
            showAlert("success", "Přihláška byla zrušena.");
            await fetchJob();
        } catch (err) {
            console.error("Chyba při rušení přihlášky:", err);
            showAlert("error", "Nepodařilo se zrušit přihlášku.");
        }
    };

    const handleApplicationStatusChange = async (applicationId, newStatus) => {
        try {
            await axiosInstance.put(
                `/api/jobs/${id}/applications/${applicationId}`,
                { status: newStatus },
            );
            showAlert("success", "Stav přihlášení byl aktualizován.");
            await fetchApplications();
            await fetchJob();
        } catch (err) {
            console.error("Chyba při aktualizaci stavu:", err);
            showAlert("error", "Nepodařilo se aktualizovat stav přihlášení.");
        }
    };

    const handleOpenChatWithApplicant = (userId) => {
        openChatWith(userId);
    };

    if (loading && !job) {
        return (
            <div className="loader_container">
                <div className="loader"></div>
            </div>
        );
    }

    if (!job) {
        return (
            <div className="no-results_listing-container">
                <h2 className="no-results_listing">
                    Tato nabídka neexistuje nebo byla trvale odstraněna.
                </h2>
            </div>
        );
    }

    const positions = job.positions || [];
    const isPositionFullyBooked = (position) =>
        position.confirmed_count >= position.required_count;

    const getPositionApplicants = (positionId) => {
        if (!applicationsData?.positions) return [];
        const pos = applicationsData.positions.find(
            (p) => String(p.position_id) === String(positionId),
        );
        return pos?.applications || [];
    };

    return (
        <>
            <Path mode="jobs" name={job.title} />

            {/* SOS warning */}
            {job.priority_offer && (
                <div className="deleted-warning-bar strong">
                    ⚡ Naléhavá nabídka – rychlá odpověď je směrem k výběru
                </div>
            )}

            <div className="job_detail_container">
                <div className="job_detail_main">
                    {/* UserMoreInfo obsahuje: název, datum, lokalita, popis, galerie, akce */}
                    <UserMoreInfo
                        isJob
                        jobId={job.id}
                        isLoggedUser={isOwner}
                        firstName={job.title}
                        lastName=""
                        bio={job.description}
                        location={job.city}
                        jobLocation={`${job.city || ""}${job.location_name ? ` · ${job.location_name}` : ""}`}
                        createdAt={job.created_at}
                        updatedAt={job.updated_at}
                        riderImagesFromDb={job.images}
                        isJobActive={job.status === "published"}
                        onEditJobClick={handleEditJobClick}
                        onShareClick={handleShareJobClick}
                        onToggleJobActive={handleToggleJobActive}
                        onDeleteJobClick={handleDeleteJobClick}
                        onRefresh={fetchJob}
                        setLoading={setLoading}
                    />

                    {/* 2. Seznam pozic - rozdělené na Vybranou a Další pozice */}
                    {positions.length > 0 && (
                        <div className="user_more_info_section">
                            <h2 className="job_detail_section_title">Pozice</h2>

                            <div className="job_detail_positions">
                                {/* Vybraná pozice */}
                                {selectedPositionId && (
                                    <>
                                        <h3 className="job_detail_subsection_title">
                                            Vybraná pozice
                                        </h3>
                                        {positions
                                            .filter(
                                                (p) =>
                                                    String(p.id) ===
                                                    String(selectedPositionId),
                                            )
                                            .map((position) => (
                                                <JobItem
                                                    key={position.id}
                                                    id={position.id}
                                                    title={job.title}
                                                    starts_at={position.starts_on}
                                                    starts_time={position.starts_time}
                                                    ends_at={position.ends_on}
                                                    ends_time={position.ends_time}
                                                    city={job.city}
                                                    location_name={job.location_name}
                                                    specialization={
                                                        position.role_label || position.role
                                                    }
                                                    payMin={position.pay_amount}
                                                    payMax={position.pay_max}
                                                    payType={position.pay_type}
                                                    isSos={position.priority_offer}
                                                    license={position.license_required || ""}
                                                    confirmedCount={position.confirmed_count || 0}
                                                    requiredCount={position.capacity || 0}
                                                    positionId={position.id}
                                                    isFavouritePositionInitially={
                                                        position.is_favourite
                                                    }
                                                    onInterestClick={(e) =>
                                                        handleInterestClick(position, e)
                                                    }
                                                    onCancelInterest={(e) =>
                                                        handleCancelInterest(position, e)
                                                    }
                                                    onShareClick={() =>
                                                        handleShareClick(position.id)
                                                    }
                                                    isPositionBooked={isPositionFullyBooked(position)}
                                                    isUserApplied={position.is_user_applied || optimisticallyApplied[position.id]}
                                                    isOwner={isOwner}
                                                    applicants={getPositionApplicants(position.id)}
                                                    onApplicationAction={handleApplicationStatusChange}
                                                    onOpenChatWithApplicant={handleOpenChatWithApplicant}
                                                    onClick={() =>
                                                        !isPositionFullyBooked(position) &&
                                                        !position.is_user_applied &&
                                                        !(optimisticallyApplied[position.id]) &&
                                                        handlePositionClick(position.id)
                                                    }
                                                />
                                            ))}
                                    </>
                                )}

                                {/* Další pozice */}
                                <h3 className="job_detail_subsection_title">
                                    Další pozice
                                </h3>
                                {positions
                                    .filter(
                                        (p) =>
                                            String(p.id) !==
                                            String(selectedPositionId),
                                    )
                                    .sort((a, b) => {
                                        const dateA = a.starts_on || "";
                                        const dateB = b.starts_on || "";
                                        if (dateA !== dateB) {
                                            return dateA.localeCompare(dateB);
                                        }
                                        const timeA = a.starts_time || "";
                                        const timeB = b.starts_time || "";
                                        return timeA.localeCompare(timeB);
                                    })
                                    .map((position) => (
                                <JobItem
                                            key={position.id}
                                            id={position.id}
                                            title={job.title}
                                            starts_at={position.starts_on}
                                            starts_time={position.starts_time}
                                            ends_at={position.ends_on}
                                            ends_time={position.ends_time}
                                            city={job.city}
                                            location_name={job.location_name}
                                            specialization={
                                                position.role_label || position.role
                                            }
                                            payMin={position.pay_amount}
                                            payMax={position.pay_max}
                                            payType={position.pay_type}
                                            isSos={position.priority_offer}
                                            license={position.license_required || ""}
                                            confirmedCount={position.confirmed_count || 0}
                                            requiredCount={position.capacity || 0}
                                            positionId={position.id}
                                            isFavouritePositionInitially={
                                                position.is_favourite
                                            }
                                            onShareClick={() =>
                                                handleShareClick(position.id)
                                            }
                                            isPositionBooked={isPositionFullyBooked(position)}
                                            isUserApplied={position.is_user_applied || optimisticallyApplied[position.id]}
                                            isOwner={isOwner}
                                            applicants={getPositionApplicants(position.id)}
                                            onApplicationAction={handleApplicationStatusChange}
                                            onOpenChatWithApplicant={handleOpenChatWithApplicant}
                                            onClick={() =>
                                                !isPositionFullyBooked(position) &&
                                                !position.is_user_applied &&
                                                !(optimisticallyApplied[position.id]) &&
                                                handlePositionClick(position.id)
                                            }
                                        />
                                    ))}
                            </div>
                        </div>
                    )}

                    {/* JobApplicationsManager pro majitele */}
                    {isOwner && applicationsData && (
                        <JobApplicationsManager
                            positions={applicationsData.positions || []}
                            onStatusChange={handleApplicationStatusChange}
                            onOpenChat={handleOpenChatWithApplicant}
                        />
                    )}
                </div>

                {/* Pravý panel - UserBasicInfo */}
                <div className="job_detail_sidebar">
                    <UserBasicInfo
                        isTechDetail={true}
                        currentUser={isOwner}
                        userId={job.user_id}
                        firstName={job.user?.first_name || job.user?.first_name || ""}
                        lastName={job.user?.last_name || ""}
                        profileImage={
                            job.user?.profile_image_url ||
                            job.user_profile_image_url ||
                            ASSETS.default_avatar
                        }
                        rating={job.user?.review_value || job.user_review_value || 0}
                        obs_email={job.user?.obs_email}
                        obs_phone={job.user?.obs_phone}
                        email={job.user?.email}
                        phone={job.user?.phone}
                        phoneVisible={job.user?.phone_visible}
                        specs={job.user?.specs || []}
                        techTitle={job.title}
                        techId={job.id}
                        socialProofInitially={job.social_proof}
                        isVerified={job.user?.is_verified || job.user_is_verified}
                    />
                </div>
            </div>
        </>
    );
};

export default JobDetail;
